import { cn } from '@/shared/lib/utils';
import { Icon } from '@/shared/ui/icon';

import { usePhxTheme } from './usePhxTheme';

export interface ModeSwitcherProps {
  className?: string;
}

/**
 * Phoenix light/dark mode switch — a pill toggle whose sliding thumb carries a
 * sun (light) or moon (dark) glyph.  Reads & writes the `data-phx-mode`
 * attribute via `usePhxTheme`, persisting the selection in `localStorage`.
 */
export function ModeSwitcher({ className }: ModeSwitcherProps) {
  const { mode, toggleMode } = usePhxTheme();
  const isDark = mode === 'dark';

  return (
    <button
      type="button"
      role="switch"
      aria-checked={isDark}
      onClick={toggleMode}
      title={`Switch to ${isDark ? 'light' : 'dark'} mode`}
      aria-label={`Switch to ${isDark ? 'light' : 'dark'} mode`}
      className={cn(
        'phx-mode-switcher border-phx-border bg-phx-surface-2 relative inline-flex h-6 w-11 cursor-pointer items-center rounded-full border p-0 transition-colors',
        className
      )}
    >
      {/* Track icons sit behind the thumb, dimmed; the active side is covered by the thumb. */}
      <span
        aria-hidden
        className="text-phx-text-muted pointer-events-none absolute inset-0 flex items-center justify-between px-1"
      >
        <Icon
          name="sun"
          size={11}
          className={cn('transition-opacity', isDark ? 'opacity-50' : 'opacity-0')}
        />
        <Icon
          name="moon"
          size={11}
          className={cn('transition-opacity', isDark ? 'opacity-0' : 'opacity-50')}
        />
      </span>
      {/* Sliding thumb carrying the current-mode glyph. */}
      <span
        aria-hidden
        className={cn(
          'bg-phx-surface shadow-phx-sm relative z-10 inline-flex h-5 w-5 items-center justify-center rounded-full transition-transform',
          isDark ? 'translate-x-[22px]' : 'translate-x-[2px]'
        )}
      >
        <Icon
          name={isDark ? 'moon' : 'sun'}
          size={12}
          className={isDark ? 'text-phx-primary' : 'text-phx-warning'}
        />
      </span>
    </button>
  );
}
